import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Group, Layer, Line, Rect, Stage } from "react-konva";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Maximize,
  Search,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_API_URL || "https://lipikar.cse.iitd.ac.in/api/ocr";
const BACKEND_URL_IMAGE =
  process.env.REACT_APP_API_MEDIA_URL || "https://lipikar.cse.iitd.ac.in/media";

const CANVAS_SELECTION_STYLE = {
  stroke: "#1976d2",
  fill: "rgba(25, 118, 210, 0.24)",
};

const CANVAS_HOVER_STYLE = {
  stroke: "#00897b",
  fill: "rgba(0, 137, 123, 0.2)",
};

const getWordText = (detection) => {
  if (Array.isArray(detection?.text)) {
    return detection.text[0] || "";
  }
  return typeof detection?.text === "string" ? detection.text : "";
};

/** Backend may store indices on `text_bbox` (legacy) or on the detection root. */
const getLineIndex = (detection) => {
  const value = detection?.text_bbox?.line_index ?? detection?.line_index;
  return typeof value === "number" && value >= 0 ? value : null;
};

const getWordIndex = (detection) => {
  const value = detection?.text_bbox?.word_index ?? detection?.word_index;
  return typeof value === "number" && value >= 0 ? value : null;
};

/**
 * When every word has a line_index, group lines and order words by word_index (with x tie-break).
 * Returns null if structured indices are not present for all items.
 */
const buildLinesFromIndices = (detections) => {
  if (!detections.length) {
    return [];
  }

  if (!detections.every((d) => getLineIndex(d) !== null)) {
    return null;
  }

  const byLine = new Map();
  for (const detection of detections) {
    const lineIndex = getLineIndex(detection);
    if (!byLine.has(lineIndex)) {
      byLine.set(lineIndex, []);
    }
    byLine.get(lineIndex).push(detection);
  }

  const sortedLineKeys = [...byLine.keys()].sort((a, b) => a - b);

  return sortedLineKeys.map((lineKey) => {
    const words = byLine.get(lineKey);
    const allHaveWordIndex = words.every((w) => getWordIndex(w) !== null);

    return [...words].sort((a, b) => {
      if (allHaveWordIndex) {
        const wa = getWordIndex(a);
        const wb = getWordIndex(b);
        if (wa !== wb) {
          return wa - wb;
        }
      }
      const ax = a.text_bbox?.x_min ?? 0;
      const bx = b.text_bbox?.x_min ?? 0;
      return ax - bx;
    });
  });
};

const TextDetectionViewer = () => {
  const { id: inferenceId } = useParams();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [allDetections, setAllDetections] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredWordId, setHoveredWordId] = useState(null);
  const [selectedWordIds, setSelectedWordIds] = useState(() => new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [zoomFactor, setZoomFactor] = useState(0);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
    naturalWidth: 0,
    naturalHeight: 0,
  });
  const [copyMessageOpen, setCopyMessageOpen] = useState(false);

  const imageRef = useRef(null);
  const textPanelRef = useRef(null);

  useEffect(() => {
    const fetchDetections = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/detections/viewer/?uploadId=${inferenceId}`);
        const data = await response.json();

        if (data.success && data.result?.detections) {
          setAllDetections(
            data.result.detections.map((item) => ({
              id: item.id,
              image_filename: item.image_filename,
              original_detections: item.original_detections,
              detections: item.detections,
              document_parser: item.document_parser,
              parsing_postprocessor: item.parsing_postprocessor,
              text_recognizer: item.text_recognizer,
            }))
          );
        } else {
          setError("Invalid data format received from server.");
        }
      } catch (fetchError) {
        console.error("Error fetching detections:", fetchError);
        setError("Failed to load text detections.");
      } finally {
        setLoading(false);
      }
    };

    fetchDetections();
  }, [inferenceId]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries[0] && imageRef.current) {
        setImageDimensions({
          width: imageRef.current.clientWidth,
          height: imageRef.current.clientHeight,
          naturalWidth: imageRef.current.naturalWidth,
          naturalHeight: imageRef.current.naturalHeight,
        });
      }
    });

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => observer.disconnect();
  }, [currentIndex, allDetections]);

  const currentResult = allDetections[currentIndex];

  const parsedLines = useMemo(() => {
    try {
      if (!currentResult?.detections) {
        return [];
      }

      const detections = JSON.parse(currentResult.detections);
      const filteredDetections = detections.filter((detection) => detection.text_bbox);

      const indexedLines = buildLinesFromIndices(filteredDetections);
      if (indexedLines) {
        return indexedLines;
      }

      filteredDetections.sort((a, b) => a.text_bbox.y_min - b.text_bbox.y_min);

      const lines = [];
      let currentLine = [];
      let currentLineMid = null;

      filteredDetections.forEach((detection) => {
        const midpoint = (detection.text_bbox.y_min + detection.text_bbox.y_max) / 2;

        if (currentLine.length === 0) {
          currentLine = [detection];
          currentLineMid = midpoint;
          return;
        }

        if (Math.abs(midpoint - currentLineMid) < 15) {
          currentLine.push(detection);
          return;
        }

        currentLine.sort((a, b) => a.text_bbox.x_min - b.text_bbox.x_min);
        lines.push(currentLine);
        currentLine = [detection];
        currentLineMid = midpoint;
      });

      if (currentLine.length > 0) {
        currentLine.sort((a, b) => a.text_bbox.x_min - b.text_bbox.x_min);
        lines.push(currentLine);
      }

      return lines;
    } catch (parseError) {
      console.error("Error parsing detections:", parseError);
      return [];
    }
  }, [currentResult]);

  const filteredLines = useMemo(
    () =>
      parsedLines.filter((line) => {
        if (!searchTerm) {
          return true;
        }

        const lineText = line.map(getWordText).join(" ").toLowerCase();
        return lineText.includes(searchTerm.toLowerCase());
      }),
    [parsedLines, searchTerm]
  );

  const rightPaneText = useMemo(
    () =>
      filteredLines
        .map((line) => line.map(getWordText).filter(Boolean).join(" ").trim())
        .filter(Boolean)
        .join("\n"),
    [filteredLines]
  );

  const scaleX = imageDimensions.naturalWidth
    ? imageDimensions.width / imageDimensions.naturalWidth
    : 1;
  const scaleY = imageDimensions.naturalHeight
    ? imageDimensions.height / imageDimensions.naturalHeight
    : 1;

  const resetSelection = () => {
    setSelectedWordIds(new Set());
    setHoveredWordId(null);
    setZoomFactor(0);
    setImageDimensions({ width: 0, height: 0, naturalWidth: 0, naturalHeight: 0 });
  };

  const handleNext = () => {
    if (currentIndex < allDetections.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      resetSelection();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      resetSelection();
    }
  };

  const handleZoomIn = () => {
    const currentScale =
      zoomFactor === 0 && imageDimensions.naturalWidth
        ? imageDimensions.width / imageDimensions.naturalWidth
        : zoomFactor || 1;
    setZoomFactor(currentScale * 1.25);
  };

  const handleZoomOut = () => {
    const currentScale =
      zoomFactor === 0 && imageDimensions.naturalWidth
        ? imageDimensions.width / imageDimensions.naturalWidth
        : zoomFactor || 1;
    setZoomFactor(Math.max(0.1, currentScale * 0.8));
  };

  const handleZoomFit = () => setZoomFactor(0);

  const selectSingleWord = (wordIdStr) => {
    setSelectedWordIds(wordIdStr ? new Set([wordIdStr]) : new Set());

    if (!wordIdStr) {
      return;
    }

    const element = document.getElementById(`detection-word-${wordIdStr}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleCanvasInteraction = (wordIdStr, isSelected) => {
    selectSingleWord(isSelected ? null : wordIdStr);
  };

  const handleTextWordClick = (wordIdStr, isSelected) => {
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      return;
    }

    setSelectedWordIds(isSelected ? new Set() : new Set([wordIdStr]));
  };

  const handleTextSelection = useCallback(() => {
    const textPanel = textPanelRef.current;
    const selection = window.getSelection();

    if (!textPanel || !selection || selection.isCollapsed || selection.rangeCount === 0) {
      return;
    }

    if (!textPanel.contains(selection.anchorNode) || !textPanel.contains(selection.focusNode)) {
      return;
    }

    const range = selection.getRangeAt(0);
    const selectedIds = new Set();

    textPanel.querySelectorAll("[data-word-id]").forEach((wordElement) => {
      try {
        if (range.intersectsNode(wordElement)) {
          selectedIds.add(wordElement.dataset.wordId);
        }
      } catch (selectionError) {
        // Some browsers can throw for detached nodes while selection is settling.
      }
    });

    if (selectedIds.size > 0) {
      setSelectedWordIds(selectedIds);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", handleTextSelection);
    return () => document.removeEventListener("selectionchange", handleTextSelection);
  }, [handleTextSelection]);

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(rightPaneText);
      setCopyMessageOpen(true);
    } catch (copyError) {
      console.error("Failed to copy text:", copyError);
      setError("Unable to copy detected text.");
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setLoading(true);

      const payload = {
        uploadId: inferenceId,
        pageNumbers: Array.from({ length: allDetections.length }, (_, index) => index + 1),
        pdfGenerationMode: "transparentText",
      };

      const response = await fetch(`${BACKEND_URL}/utils/viewer/generate-pdf/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || "Failed to generate PDF.");
      }

      if (data.success && data.result?.mediaUrl) {
        window.open(`${BACKEND_URL_IMAGE}/${data.result.mediaUrl}`, "_blank");
        return;
      }

      throw new Error("No PDF URL received from server.");
    } catch (downloadError) {
      console.error("Error generating PDF:", downloadError);
      setError(downloadError.message || "Failed to generate PDF.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && allDetections.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        backgroundColor: theme.palette.background.default,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.25, sm: 2 },
          py: { xs: 1, sm: 1.25 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1,
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 700,
            fontSize: "0.9rem",
            wordBreak: "break-word",
          }}
        >
          {currentResult?.image_filename || "No image"}
          <span style={{ color: "#7b8794", fontWeight: 500, marginLeft: 8 }}>
            ({currentIndex + 1} / {allDetections.length})
          </span>
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "stretch", sm: "flex-end" },
            gap: 0.75,
            flexWrap: "wrap",
            width: { xs: "100%", sm: "auto" },
            "& .MuiButton-root": {
              flex: { xs: "1 1 calc(50% - 6px)", sm: "0 0 auto" },
            },
          }}
        >
          <Button
            onClick={handlePrevious}
            disabled={currentIndex === 0 || loading}
            size="small"
            sx={{ textTransform: "none", minWidth: 0, px: 1.25 }}
          >
            <ChevronLeft size={16} />
            Prev
          </Button>
          <Button
            onClick={handleNext}
            disabled={currentIndex === allDetections.length - 1 || loading}
            size="small"
            sx={{ textTransform: "none", minWidth: 0, px: 1.25 }}
          >
            Next
            <ChevronRight size={16} />
          </Button>
          <Button
            onClick={handleCopyText}
            disabled={!rightPaneText}
            variant="outlined"
            size="small"
            sx={{ textTransform: "none" }}
          >
            <Copy size={14} style={{ marginRight: 6 }} />
            Copy Page Text
          </Button>
          <Button
            onClick={handleDownloadPDF}
            disabled={loading}
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              backgroundColor: "#1565c0",
              "&:hover": { backgroundColor: "#0d47a1" },
            }}
          >
            <Download size={14} style={{ marginRight: 6 }} />
            {loading ? "Generating PDF..." : "Download Overlayed Text PDF"}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          flexGrow: 1,
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flex: { xs: "1 1 52%", md: "0 0 60%" },
            backgroundColor: isDarkMode ? "#1f2328" : "#d9dfdd",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            overflow: "auto",
            minHeight: 0,
            p: { xs: 1.25, sm: 2, md: 3 },
            position: "relative",
          }}
        >
          <Paper
            sx={{
              position: "absolute",
              bottom: { xs: 12, sm: 24 },
              right: { xs: 12, sm: 24 },
              display: "flex",
              gap: 0.5,
              padding: 0.5,
              borderRadius: 2,
              zIndex: 50,
              backgroundColor: isDarkMode ? "rgba(30, 30, 30, 0.92)" : "rgba(255, 255, 255, 0.92)",
            }}
            elevation={3}
          >
            <Tooltip title="Zoom Out">
              <IconButton onClick={handleZoomOut} size="small">
                <ZoomOut size={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fit Width">
              <IconButton onClick={handleZoomFit} size="small" color={zoomFactor === 0 ? "primary" : "default"}>
                <Maximize size={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom In">
              <IconButton onClick={handleZoomIn} size="small">
                <ZoomIn size={18} />
              </IconButton>
            </Tooltip>
          </Paper>

          <Box
            sx={{
              position: "relative",
              display: "block",
              width: zoomFactor === 0 ? "100%" : "max-content",
              maxWidth: zoomFactor === 0 ? "100%" : "none",
            }}
          >
            {currentResult?.image_filename ? (
              <img
                ref={imageRef}
                src={`${BACKEND_URL_IMAGE}/detection_images/${currentResult.image_filename}`}
                alt="Document Workspace"
                onLoad={(event) => {
                  setImageDimensions({
                    width: event.target.clientWidth,
                    height: event.target.clientHeight,
                    naturalWidth: event.target.naturalWidth,
                    naturalHeight: event.target.naturalHeight,
                  });
                }}
                style={{
                  width: zoomFactor === 0 ? "100%" : `${(imageDimensions.naturalWidth || 800) * zoomFactor}px`,
                  maxWidth: zoomFactor === 0 ? "100%" : "none",
                  height: "auto",
                  display: "block",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.14)",
                  borderRadius: 12,
                }}
              />
            ) : null}

            {imageDimensions.width > 0 && imageDimensions.height > 0 ? (
              <Stage
                width={imageDimensions.width}
                height={imageDimensions.height}
                style={{ position: "absolute", top: 0, left: 0 }}
              >
                <Layer scaleX={scaleX} scaleY={scaleY}>
                  {parsedLines.map((line, lineIdx) => (
                    <Group key={`line-group-${lineIdx}`}>
                      {line.map((detection, wordIdx) => {
                        const wordId = `${lineIdx}-${wordIdx}`;
                        const isHovered = hoveredWordId === wordId;
                        const isSelected = selectedWordIds.has(wordId);
                        const strokeColor = isSelected
                          ? CANVAS_SELECTION_STYLE.stroke
                          : isHovered
                            ? CANVAS_HOVER_STYLE.stroke
                            : "rgba(240, 98, 146, 0.82)";
                        const fillColor = isSelected
                          ? CANVAS_SELECTION_STYLE.fill
                          : isHovered
                            ? CANVAS_HOVER_STYLE.fill
                            : "transparent";
                        const strokeWidth = isSelected || isHovered ? 3 : 1.5;

                        const eventHandlers = {
                          onMouseEnter: () => {
                            setHoveredWordId(wordId);
                            document.body.style.cursor = "pointer";
                          },
                          onMouseLeave: () => {
                            setHoveredWordId(null);
                            document.body.style.cursor = "default";
                          },
                          onClick: () => handleCanvasInteraction(wordId, isSelected),
                        };

                        if (detection.polygon) {
                          return (
                            <Line
                              key={wordId}
                              points={detection.polygon.flat()}
                              closed
                              stroke={strokeColor}
                              strokeWidth={strokeWidth}
                              fill={fillColor}
                              hitStrokeWidth={10}
                              strokeScaleEnabled={false}
                              {...eventHandlers}
                            />
                          );
                        }

                        if (detection.text_bbox) {
                          const bbox = detection.text_bbox;
                          return (
                            <Rect
                              key={wordId}
                              x={bbox.x_min}
                              y={bbox.y_min}
                              width={bbox.x_max - bbox.x_min}
                              height={bbox.y_max - bbox.y_min}
                              stroke={strokeColor}
                              strokeWidth={strokeWidth}
                              fill={fillColor}
                              hitStrokeWidth={10}
                              strokeScaleEnabled={false}
                              {...eventHandlers}
                            />
                          );
                        }

                        return null;
                      })}
                    </Group>
                  ))}
                </Layer>
              </Stage>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            flex: { xs: "1 1 48%", md: "0 0 40%" },
            display: "flex",
            flexDirection: "column",
            borderLeft: { xs: "none", md: `1px solid ${theme.palette.divider}` },
            borderTop: { xs: `1px solid ${theme.palette.divider}`, md: "none" },
            backgroundColor: theme.palette.background.paper,
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              p: { xs: 1.25, sm: 2 },
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: "sticky",
              top: 0,
              backgroundColor: theme.palette.background.paper,
              zIndex: 10,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
              Detected Text
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search detected text..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              sx={{ display: { xs: "none", sm: "block" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={theme.palette.text.secondary} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "#f7f7f5",
                },
              }}
            />
          </Box>

          <Box
            ref={textPanelRef}
            onMouseUp={handleTextSelection}
            onMouseMove={handleTextSelection}
            onKeyUp={handleTextSelection}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              p: { xs: 1.25, sm: 2.5 },
              backgroundColor: theme.palette.background.default,
            }}
          >
            {filteredLines.length === 0 ? (
              <Alert severity="info">No detected text matched your search.</Alert>
            ) : (
              filteredLines.map((line) => {
                const originalLineIndex = parsedLines.indexOf(line);
                if (line.length === 0) {
                  return null;
                }

                return (
                  <Box
                    key={`text-line-${originalLineIndex}`}
                    sx={{
                      mb: 1.5,
                      p: 1.25,
                      borderRadius: 2,
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.7)",
                    }}
                  >
                    {line.map((detection, wordIdx) => {
                      const wordText = getWordText(detection);
                      if (!wordText) {
                        return null;
                      }

                      const wordId = `${originalLineIndex}-${wordIdx}`;
                      const isHovered = hoveredWordId === wordId;
                      const isSelected = selectedWordIds.has(wordId);

                      return (
                        <span
                          id={`detection-word-${wordId}`}
                          data-word-id={wordId}
                          key={wordId}
                          onMouseEnter={() => setHoveredWordId(wordId)}
                          onMouseLeave={() => setHoveredWordId(null)}
                          onClick={() => handleTextWordClick(wordId, isSelected)}
                          style={{
                            cursor: "pointer",
                            transition: "background-color 0.1s ease",
                            backgroundColor: isSelected
                              ? isDarkMode
                                ? "#1976d2"
                                : "#dbeafe"
                              : isHovered
                                ? isDarkMode
                                  ? "#333333"
                                  : "#eef2f7"
                                : "transparent",
                            color: isSelected
                              ? isDarkMode
                                ? "#ffffff"
                                : "#1e3a8a"
                              : theme.palette.text.primary,
                            fontWeight: isSelected ? 700 : 500,
                            fontSize: isSmallScreen ? "0.84rem" : "0.96rem",
                            lineHeight: 1.85,
                            fontFamily: '"Courier New", Courier, monospace',
                            padding: "2px 4px",
                            borderRadius: "4px",
                            marginRight: "4px",
                          }}
                        >
                          {wordText}
                        </span>
                      );
                    })}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={copyMessageOpen}
        autoHideDuration={2500}
        onClose={() => setCopyMessageOpen(false)}
        message="Detected text copied to clipboard."
      />
    </Box>
  );
};

export default TextDetectionViewer;
